import type { Data } from '@puckeditor/core';
import { useQuery } from '@apollo/client';

import { CmsResolveContentDocument, CmsResolveWebsiteContentDocument } from 'src/gql/graphql';

import { isPuckData } from './puck-utils';
import { useCmsVersionPreview } from './preview';

type CmsResolvedContentVariables = {
  dispensaryId?: string;
  enterpriseId: string | undefined;
  websiteId?: string;
  urlPath?: string;
};

type CmsResolvedContent = {
  data: Data | undefined;
  loading: boolean;
};

/**
 * Resolves published CMS content for a model via targeting (dispensaryId/urlPath), using explicit
 * website scope when websiteId is available and the enterprise compatibility route otherwise.
 * Transparently substitutes a specific version's data when `cmsModelId`/`cmsVersionId` query params
 * match this model (see `useCmsVersionPreview`). Both fetch paths return untyped JSON on the
 * wire, so `data` is validated with `isPuckData` here regardless of which source served it --
 * callers get one `Data | undefined` shape either way, never a distinct "resolved" vs.
 * "versioned" content type.
 */
export function useCmsResolvedContent(modelId: string, variables: CmsResolvedContentVariables): CmsResolvedContent {
  const { dispensaryId, enterpriseId, websiteId, urlPath } = variables;
  const preview = useCmsVersionPreview(modelId, enterpriseId);
  const useWebsiteScope = Boolean(websiteId);

  const { data: compatibilityData, loading: compatibilityLoading } = useQuery(CmsResolveContentDocument, {
    variables: { enterpriseId: enterpriseId ?? '', modelId, dispensaryId, urlPath },
    skip: !enterpriseId || preview.isPreviewing || useWebsiteScope,
  });
  const { data: websiteData, loading: websiteLoading } = useQuery(CmsResolveWebsiteContentDocument, {
    variables: { enterpriseId: enterpriseId ?? '', websiteId: websiteId ?? '', modelId, dispensaryId, urlPath },
    skip: !enterpriseId || !websiteId || preview.isPreviewing,
  });

  if (preview.isPreviewing) {
    return { data: preview.data, loading: preview.loading };
  }

  const data = useWebsiteScope
    ? websiteData?.cmsResolveWebsiteContent?.data
    : compatibilityData?.cmsResolveContent?.data;
  const loading = useWebsiteScope ? websiteLoading : compatibilityLoading;
  return { data: isPuckData(data) ? data : undefined, loading };
}
